import { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api.js';
import { useAuth } from '../hooks/useAuth.jsx';
import { AuthShell } from '../components/AuthShell.jsx';

/**
 * /verify?token=... — magic-link landing page.
 *
 * Flow:
 *  1. Read token from query string
 *  2. POST it to /api/signup/verify
 *  3. Backend sets email_verified_at, issues session cookie, returns admin
 *  4. Call auth.refresh() to hydrate the context
 *  5. Redirect to /dashboard
 *
 * Why a POST (not a GET)?
 *  Email scanners and link-preview bots follow magic links with GETs. If
 *  the verify handler was a GET, the first click by Microsoft/Google link
 *  inspection would consume the token before the user ever saw it. By
 *  requiring a POST (which this page issues from JS), we make the token
 *  survive until the human actually lands here.
 *
 * StrictMode guard:
 *  React 18 dev mode intentionally runs effects twice to catch bugs. That
 *  would cause /verify to POST twice, and the second POST would fail
 *  because the token is now consumed. The `didRun` ref makes verification
 *  idempotent at the component level.
 */
export function VerifyPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const didRun = useRef(false);

  const [status, setStatus] = useState('verifying');  // 'verifying' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    if (didRun.current) return;
    didRun.current = true;

    const token = params.get('token');
    if (!token) {
      setStatus('error');
      setErrorMessage('No verification token in the link. Please check the email and try again.');
      return;
    }

    (async () => {
      try {
        await api.post('/api/signup/verify', { token });
        // Hydrate auth context — the session cookie is now set
        await refresh();
        setStatus('success');

        // Short delay before navigating so the success message is visible.
        // Feels more reassuring than an instant redirect.
        setTimeout(() => navigate('/dashboard', { replace: true }), 1200);
      } catch (e) {
        setStatus('error');
        if (e instanceof ApiError) {
          setErrorMessage(e.message);
        } else {
          setErrorMessage('Something went wrong verifying your email. Please try again.');
        }
      }
    })();
  }, [params, navigate, refresh]);

  return (
    <AuthShell>
      <div className="auth-card text-center">
        {status === 'verifying' && <VerifyingView />}
        {status === 'success' && <SuccessView />}
        {status === 'error' && <ErrorView message={errorMessage} />}
      </div>
    </AuthShell>
  );
}

function VerifyingView() {
  return (
    <>
      <div
        aria-hidden="true"
        style={{
          width: 56, height: 56, borderRadius: '50%',
          border: '3px solid var(--line)',
          borderTopColor: 'var(--tocyn-green)',
          margin: '0 auto var(--sp-5)',
          animation: 'spin 0.8s linear infinite',
        }}
      />
      <h1 className="auth-heading">Verifying your email…</h1>
      <p className="auth-subheading">
        Just a moment. We're confirming your link and logging you in.
      </p>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </>
  );
}

function SuccessView() {
  return (
    <>
      <div
        aria-hidden="true"
        style={{
          width: 56, height: 56, borderRadius: '50%',
          background: 'var(--tocyn-green-l)',
          color: 'var(--tocyn-green-d)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 28, fontWeight: 700,
          margin: '0 auto var(--sp-5)',
        }}
      >
        ✓
      </div>
      <h1 className="auth-heading">You're in.</h1>
      <p className="auth-subheading">
        Taking you to your dashboard now…
      </p>
    </>
  );
}

function ErrorView({ message }) {
  return (
    <>
      <div
        aria-hidden="true"
        style={{
          width: 56, height: 56, borderRadius: '50%',
          background: 'var(--id-red-l)',
          color: 'var(--id-red-d)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 32, fontWeight: 700,
          margin: '0 auto var(--sp-5)',
          fontFamily: 'var(--font-display)',
        }}
      >
        !
      </div>
      <h1 className="auth-heading">We couldn't verify that link.</h1>
      <p className="auth-subheading">
        {message || 'The link may have expired or already been used.'}
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)', marginTop: 'var(--sp-5)' }}>
        <Link to="/login" className="btn btn-primary btn-block">
          Try logging in
        </Link>
        <Link to="/signup" className="btn btn-ghost btn-block">
          Or sign up again
        </Link>
      </div>

      <p className="muted" style={{ fontSize: 'var(--fs-xs)', marginTop: 'var(--sp-5)' }}>
        Need help? Email <a href="mailto:jez@tocynid.com">jez@tocynid.com</a>.
      </p>
    </>
  );
}
